/**
 * A drop zone with a Choose file button (docs/ux/registry.md): accepted
 * types and the limit in the label, the client checks both before a byte
 * leaves, a real progress bar from the request, Cancel aborts, refusals are
 * inline, and a landed file shows its preview or its name with who added it.
 * An upload never replaces: the copy says the earlier version is kept. The
 * doctor's assets go through `uploadAsset`; any other target (the practice's
 * bands) passes its own `send`.
 */

import { cn } from 'cn';
import { useId, useRef, useState, type DragEvent } from 'react';
import type { DoctorView } from '@/app/types';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { UploadRefused, uploadAsset, type UploadKind } from './useRegistry';

export type UploadFieldProps<T> = {
  /** The doctor and the asset, when `send` is not given. */
  slug?: string;
  target?: UploadKind;
  /** The request itself, for uploads that are not a doctor's asset. */
  send?: (file: File, onProgress: (percent: number) => void, signal: AbortSignal) => Promise<T>;
  label: string;
  /** Accepted MIME types and the file extensions the picker offers. */
  accept: { mime: string[]; extensions: string[]; words: string };
  maxBytes: number;
  offline: boolean;
  /** What is on file now, for the landed state. */
  current?: { preview?: string; previewAlt?: string; name?: string; addedAt?: string; addedBy?: string } | null;
  onLanded: (result: T, file: File) => void;
  compact?: boolean;
  /** The sentence under the field when the default (a new version is added, earlier ones kept) does not fit. */
  help?: string;
};

const MB = 1024 * 1024;
const mb = (n: number): string => `${(n / MB).toFixed(n % MB === 0 ? 0 : 1)} MB`;

export function UploadField<T = DoctorView>({ slug, target, send, label, accept, maxBytes, offline, current, onLanded, compact, help }: UploadFieldProps<T>) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const abort = useRef<AbortController | null>(null);
  const [percent, setPercent] = useState<number | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [over, setOver] = useState(false);

  function refuse(file: File): string | null {
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    const typeOk = accept.mime.includes(file.type) || (file.type === '' && accept.extensions.includes(ext));
    if (!typeOk) return `Only ${accept.words} ${accept.mime.length === 1 ? 'is' : 'are'} accepted for the ${label.toLowerCase()}. Save the file as ${accept.extensions.join(' or ')} and try again.`;
    if (file.size > maxBytes) return `This file is ${mb(file.size)}; the limit is ${mb(maxBytes)}.`;
    return null;
  }

  async function send1(file: File) {
    setError(null);
    const refused = refuse(file);
    if (refused) { setError(refused); return; }
    const controller = new AbortController();
    abort.current = controller;
    setFileName(file.name);
    setPercent(0);
    try {
      // Without `send`, the field is a doctor's asset and T is the doctor view the server returns.
      const result = send ? await send(file, setPercent, controller.signal) : ((await uploadAsset(slug!, target!, file, setPercent, controller.signal)) as unknown as T);
      onLanded(result, file);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') setError(null);
      else if (err instanceof UploadRefused && err.reason) setError(`${err.reason.charAt(0).toUpperCase()}${err.reason.slice(1)}. Nothing changed.`);
      else setError('The upload did not complete. The registry is unchanged. Try again.');
    } finally {
      setPercent(null);
      setFileName(null);
      abort.current = null;
      if (input.current) input.current.value = '';
    }
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setOver(false);
    if (offline || percent !== null) return;
    const file = e.dataTransfer.files[0];
    if (file) void send1(file);
  }

  const busy = percent !== null;
  const isTemplate = target?.kind === 'template';
  return (
    <div data-slot="upload-field" className="flex flex-col gap-2">
      <div
        onDragOver={(e) => { e.preventDefault(); if (!offline && !busy) setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        className={cn('flex min-h-24 flex-col justify-center gap-2 rounded-lg border border-dashed border-beam-line-strong px-4 py-3 text-[13px]', over && 'bg-beam-cream-deep', compact && 'min-h-16')}
      >
        {busy ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="truncate font-medium">{fileName}</span>
              <Button type="button" variant="outline" size="sm" onClick={() => abort.current?.abort()}>Cancel upload</Button>
            </div>
            <Progress value={percent ?? 0} aria-label={`Uploading ${label.toLowerCase()}`} className="h-1" />
            <span role="status" className="text-xs text-beam-text-muted">Uploading, {percent} percent</span>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {current?.preview ? <img src={current.preview} alt={current.previewAlt ?? `${label} for this doctor`} className="max-h-24 rounded-md border border-beam-line bg-beam-paper p-1" /> : null}
              <span className="text-beam-text-muted">{current ? `${current.name ?? 'On file'}${current.addedAt ? `. Added ${current.addedAt}${current.addedBy ? ` by ${current.addedBy}` : ''}` : ''}` : `Drop ${accept.words} here, or`}</span>
              <Button type="button" variant="outline" size="default" className="h-8" data-slot="upload-choose" disabled={offline} title={offline ? 'Paused while offline.' : undefined} onClick={() => input.current?.click()} aria-describedby={`${id}-help`}>{current ? 'Replace' : 'Choose file'}</Button>
            </div>
            <p id={`${id}-help`} className="text-xs text-beam-text-muted">{help ?? `${accept.words.charAt(0).toUpperCase()}${accept.words.slice(1)}, up to ${mb(maxBytes)}. A new ${isTemplate ? 'version' : 'image'} is added; earlier ${isTemplate ? 'versions are' : 'images are'} kept.`}</p>
          </>
        )}
        <input ref={input} type="file" accept={[...accept.extensions, ...accept.mime].join(',')} className="sr-only" tabIndex={-1} aria-label={`${label}, ${accept.words}, up to ${mb(maxBytes)}`} onChange={(e) => { const f = e.target.files?.[0]; if (f) void send1(f); }} />
      </div>
      {error ? <p role="alert" className="text-[13px] text-beam-error">{error}</p> : null}
    </div>
  );
}
